/**
 * Snapshots leves de lineage de replay por sessão (min/max seq + generation).
 */

import { openQueueDb, STORE_REPLAY } from '../storage/idbQueueDb';
import { getCheckpointGeneration } from './persistentReplay';
import { getActiveReplaySession } from './replaySession';
import type { QueueItem } from '../queue/types';

const LINEAGE_PREFIX = '__lineage_';

export interface ReplayLineageSnapshot {
  kind: 'lineage';
  sessionId: string;
  minSeq: number;
  maxSeq: number;
  generation: number;
  updatedAt: number;
  replayGenerationId?: string;
  originalEventIds?: string[];
}

const memory = new Map<string, ReplayLineageSnapshot>();

function storageKey(sessionId: string): string {
  return `${LINEAGE_PREFIX}${sessionId}`;
}

async function idbPut(snapshot: ReplayLineageSnapshot): Promise<void> {
  const db = await openQueueDb();
  if (!db) return;
  const record = { ...snapshot, sessionId: storageKey(snapshot.sessionId) };
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_REPLAY, 'readwrite');
      tx.objectStore(STORE_REPLAY).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    } catch {
      resolve();
    }
  });
}

export function recordReplayLineage(items: QueueItem[]): void {
  const session = getActiveReplaySession();
  const bySession = new Map<string, QueueItem[]>();
  for (const item of items) {
    const sid = item.params.sessionId;
    if (!bySession.has(sid)) bySession.set(sid, []);
    bySession.get(sid)!.push(item);
  }
  for (const [sessionId, group] of bySession) {
    const seqs = group.map(i => i.sessionSequence);
    const minSeq = Math.min(...seqs);
    const maxSeq = Math.max(...seqs);
    const prev = memory.get(sessionId);
    const snapshot: ReplayLineageSnapshot = {
      kind: 'lineage',
      sessionId,
      minSeq: prev ? Math.min(prev.minSeq, minSeq) : minSeq,
      maxSeq: prev ? Math.max(prev.maxSeq, maxSeq) : maxSeq,
      generation: getCheckpointGeneration(sessionId),
      updatedAt: Date.now(),
      replayGenerationId: session?.replayGenerationId,
      originalEventIds: session?.originalEventIds,
    };
    memory.set(sessionId, snapshot);
    void idbPut(snapshot);
  }
}

export function getReplayLineage(sessionId: string): ReplayLineageSnapshot | null {
  return memory.get(sessionId) ?? null;
}

export function _testResetReplayLineage(): void {
  memory.clear();
}
